/* eslint-disable @typescript-eslint/no-explicit-any */
import eyeIcon from '../../../assets/svg/eye.svg?react';
import Datatable from '@/components/common/Datatable';

import {
  Box,
  Flex,
  Icon,
  Text,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalHeader,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { convertDateFormat } from './../../../utils/dateConverter';
import StatusBadge from '@/components/common/StatusBadge';
import { GroupOrder } from '@/services/OrderManagement/types';
import { Column } from 'react-table';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { useGetAllPaymentHistory } from '@/services/OrderManagement';
import GroupOrderDetails from '../GroupOrderDetails';
import { TAX_RATE } from '@/utils/constants';

interface FilterState {
  start_date?: string;
  end_date?: string;
  payment_method?: string;
  status?: boolean | null;
}

const AllOrder = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [query, setQuery] = useState<string>('');
  const [filters, setFilters] = useState<FilterState>({
    start_date: '',
    end_date: '',
    payment_method: '',
    status: null,
  });

  const { data, isLoading, isError } = useGetAllPaymentHistory({
    page: pagination.page,
    page_size: pagination.pageSize,
    search: query || undefined,
    start_date: filters.start_date || undefined,
    end_date: filters.end_date || undefined,
    payment_method: filters.payment_method || undefined,
    status: filters.status,
  });

  useEffect(() => {
    if (data) {
      setPagination((prev) => ({
        ...prev,
        pageSize: data.pageSize || 10,
        totalCount: data.count || 0,
        totalPages: data.page_count || 0,
      }));
    }
  }, [data]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSearch = (searchTerm: string) => {
    setQuery(searchTerm);
    setPagination((prev) => ({
      ...prev,
      page: 1,
      totalCount: 0,
      totalPages: 0,
    }));
  };

  const applyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const resetFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      payment_method: '',
      status: null,
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const handleViewOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrderId(null);
  };

  const flattenedData = useMemo(() => {
    if (!data?.results) return [];

    const orders: (GroupOrder & { itemsCount: number })[] = [];

    data.results.forEach((payment: any) => {
      const items = payment.purchased_items;
      const itemsCount = items.length;

      if (itemsCount === 0) return;

      const shipment_address = payment.shipment_address || {};
      const addressParts = [
        shipment_address.address_line_1,
        shipment_address.address_line_2,
        shipment_address.town_city,
        shipment_address.landmark,
        shipment_address.pincode,
        shipment_address.state_detail?.name,
        shipment_address.country_detail?.country_name,
      ]
        .filter(Boolean)
        .map((p: string) => p.trim());

      const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : '-';

      const statuses = items.map((item: any) => {
        const tracking = item.order_tracking || {};
        const statusName = tracking.order_status?.status_name || 'Processing';

        return {
          statusName,
          isUnderMaintenance: statusName === 'Under Maintenance',
          isReadyForDelivery: statusName === 'Ready for Delivery',
          isShipped: statusName === 'Shipped',
          isInWarehouse: tracking.is_in_warehouse === true,
          isOutForDelivery: statusName === 'Out for Delivery',
          isDelivered: tracking.has_delivered === true,
          isDeliveryFailed: tracking.has_delivery_failed === true,
          isCancelled: tracking.has_cancelled === true,
        };
      });

      let displayStatus = 'Not Shipped Yet';

      // 1. Under Maintenance (highest priority)
      if (statuses.some((s: any) => s.isUnderMaintenance)) {
        displayStatus = 'Under Maintenance';
      }
      // 2. All Ready for Delivery
      else if (statuses.every((s: any) => s.isReadyForDelivery)) {
        displayStatus = 'Ready for Delivery';
      }
      // 3. FINAL STATES FIRST — check this before transit flags!
      else if (statuses.every((s: any) => s.isDelivered || s.isDeliveryFailed || s.isCancelled)) {
        const hasDelivered = statuses.some((s: any) => s.isDelivered);
        const hasDeliveryFailed = statuses.some((s: any) => s.isDeliveryFailed);

        if (hasDelivered && hasDeliveryFailed) {
          displayStatus = 'Mixed Final Status';
        } else if (hasDelivered) {
          // Any Delivered (with or without Cancelled) → Delivered
          displayStatus = 'Delivered';
        } else if (hasDeliveryFailed) {
          // Any Delivery Failed (with or without Cancelled) → Delivery Failed
          displayStatus = 'Delivery Failed';
        } else {
          // Should not happen, but fallback
          displayStatus = 'Cancelled';
        }
      }
      // 4. Delivery In Progress (only if not final)
      else if (statuses.some((s: any) => s.isShipped || s.isInWarehouse || s.isOutForDelivery)) {
        displayStatus = 'Delivery In Progress';
      }
      // 5. Default
      else {
        displayStatus = 'Not Shipped Yet';
      }

      const itemsSummary = items.reduce(
        (acc: any, item: any) => {
          const txn = item.order_tracking?.individual_txn;
          const cart = item.cart_item;

          if (!txn) return acc;

          const qty = txn.quantity || cart?.quantity || 1;

          const unitPrice = parseFloat(txn.unit_price || '0');
          const customizedPrice = parseFloat(txn.customized_price || '0');
          const addOnPrice = parseFloat(txn.add_ons_price || '0');

          acc.productTotal += unitPrice * qty;
          acc.customizedTotal += customizedPrice * qty;
          acc.addOnTotal += addOnPrice * qty;

          return acc;
        },
        {
          productTotal: 0,
          customizedTotal: 0,
          addOnTotal: 0,
        },
      );
      // Calculate total quantity from cart_item.quantity
      const totalQuantity = items.reduce((sum: number, item: any) => {
        const cart = item.cart_item;
        return sum + (cart?.quantity || 0);
      }, 0);
      const baseSubtotal =
        itemsSummary.productTotal + itemsSummary.customizedTotal + itemsSummary.addOnTotal;
      const estimatedTax = baseSubtotal * TAX_RATE;
      const calculatedTotal = parseFloat((baseSubtotal + estimatedTax).toFixed(2));

      orders.push({
        id: payment.id,
        bill_id: payment.bill_id,
        order_id: payment.bill_id || '-',
        shipping_address: {
          name: shipment_address.name || '-',
          address_line_1: shipment_address.address_line_1,
          address_line_2: shipment_address.address_line_2,
          town_city: shipment_address.town_city,
          landmark: shipment_address.landmark,
          pincode: shipment_address.pincode,
          state: shipment_address.state_detail?.name,
          country: shipment_address.country_detail?.country_name,
          location: undefined,
          full_address: fullAddress,
        },
        is_return: false,
        order_status: {
          status_name: displayStatus,
        },
        total_price: calculatedTotal,
        currency: payment.currency || 'USD',
        payment: payment.payment_method?.method_name || '-',
        created_at: payment.created_at || '-',
        is_customized: items.some((i: any) => i.cart_item?.is_customized),
        customized_fields: {},
        transaction_id: payment.transaction_id,
        itemsCount: totalQuantity,
      } as GroupOrder & { itemsCount: number });
    });

    return orders;
  }, [data]);

  const columns = useMemo<Column<GroupOrder>[]>(
    () => [
      {
        Header: 'Bill ID',
        id: 'bill_id',
        accessor: (row) => row.bill_id || '-',

        Cell: ({ row }: { row: { original: GroupOrder } }) => {
          const billId = row.original.bill_id || '-';
          return (
            <Flex align="center" gap={2}>
              <Text>{billId}</Text>
            </Flex>
          );
        },
      },
      {
        Header: 'Quantity',
        id: 'quantity',
        accessor: 'itemsCount',
        Cell: ({ value }: { value: number }) => (
          <Text fontWeight="semibold" color="text.primary">
            {value} {value === 1 ? 'item' : 'items'}
          </Text>
        ),
      },
      {
        Header: 'Ship To',
        accessor: (row) => row.shipping_address?.name ?? '-',
        Cell: ({ value }: { value: string }) => <Text>{value}</Text>,
      },
      {
        Header: 'Status',
        accessor: (row) => row.order_status?.status_name ?? '-',
        Cell: ({ value }: { value: string }) => <StatusBadge status={value} />,
      },
      {
        Header: 'Payment',
        accessor: 'payment',
        Cell: ({ value }: { value: string }) => <Text>{value ?? '-'}</Text>,
      },
      {
        Header: 'Total Price',
        id: 'total_price',
        accessor: (row) => row.total_price,
        Cell: ({ row }: { row: { original: GroupOrder } }) => {
          const price = row.original.total_price ?? 0;
          const currency = row.original.currency ?? 'USD';
          const symbol = getCurrencySymbol(currency);

          return (
            <Text fontWeight="semibold" color="text.primary">
              {symbol}
              {price.toFixed(2)}
            </Text>
          );
        },
      },
      {
        Header: 'Order Placed',
        accessor: 'created_at',
        Cell: ({ value }: { value: string }) => <Text>{convertDateFormat(value)}</Text>,
      },
      {
        Header: 'Action',
        id: 'action',
        Cell: ({ row }: { row: { original: GroupOrder } }) => {
          const orderId = row.original.id;

          return (
            <Flex gap={2} align="center" minWidth="80px">
              <Tooltip label="View Group Order" placement="bottom" hasArrow>
                <Box display="inline-block">
                  <Icon
                    as={eyeIcon}
                    boxSize={5}
                    color="brand.primary.DEFAULT"
                    onClick={() => {
                      handleViewOrder(String(orderId));
                    }}
                    cursor="pointer"
                  />
                </Box>
              </Tooltip>
            </Flex>
          );
        },
      },
    ],
    [],
  );

  return (
    <Box bg="background.white">
      <Box width="auto" overflow="auto">
        <Datatable<GroupOrder>
          data={flattenedData}
          isLoading={isLoading}
          isError={isError}
          columns={columns}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            totalCount: pagination.totalCount,
            totalPages: pagination.totalPages,
          }}
          onSearch={handleSearch}
          onPageChange={handlePageChange}
          filters={filters}
          setFilters={setFilters}
          applyFilters={applyFilters}
          resetFilters={resetFilters}
          hideCategoryFilter={true}
          sticky={true}
        />
      </Box>

      <Modal size="5xl" isOpen={isModalOpen} onClose={closeModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>Order Details</ModalHeader>
          <ModalBody
            maxHeight="70vh"
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'gray.300', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'gray.400' },
            }}
          >
            {selectedOrderId ? (
              <>
                <GroupOrderDetails id={selectedOrderId} />
              </>
            ) : (
              <Text p={6}>No order selected...</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeModal}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AllOrder;
